<script setup>
import AgendaDetailCard from '~/components/blocks/two-six/agenda/detail-card.vue';

const props = defineProps({
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    infinite: { type: Boolean, default: false },
    year: { type: Number, default: 2026 },
});

const emit = defineEmits(['loadMore', 'changePage']);

const sentinel = ref(null);

const sessions = computed(() => props.data?.sessions || []);

const hasMore = computed(() => {
    if (!props.data) return false;
    const current = parseInt(props.data.current_page || 1, 10);
    const total = parseInt(props.data.pages || 1, 10);
    return current < total;
});

const isSentinelInView = () => {
    const el = sentinel.value;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const viewH = window.innerHeight;
    return rect.top < viewH + 400 && rect.bottom > -400;
};

useIntersectionObserver(
    sentinel,
    ([entry]) => {
        if (!props.infinite) return;
        if (entry?.isIntersecting && hasMore.value) emit('loadMore');
    },
    { rootMargin: '400px 0px' },
);

watch(() => props.loading, (newLoading, oldLoading) => {
    if (!props.infinite) return;
    if (oldLoading && !newLoading && hasMore.value) {
        nextTick(() => {
            if (isSentinelInView()) emit('loadMore');
        });
    }
});

// The rendered list changed (tab switch, filter change, load-more, backfill).
// Tab/filter refetches run through useAsyncData's `pending`, not `loadingMore`,
// so the `props.loading` watch above never fires for them — and a short first
// page can leave the sentinel already inside the viewport with no fresh
// intersection edge to trigger the observer. Re-check and pull the next page.
watch(() => sessions.value.length, () => {
    if (!props.infinite || props.loading) return;
    nextTick(() => {
        if (hasMore.value && isSentinelInView()) emit('loadMore');
    });
});
</script>

<template>
    <div class="detail-list" :class="{ 'pointer-events-none': loading }">

        <!-- Initial load spinner -->
        <div
            v-if="loading && sessions.length === 0"
            class="flex items-center justify-center py-[80px] w-full"
        >
            <FragmentsLoading loading />
        </div>

        <!-- Grid (removals apply immediately). -->
        <ul
            v-else-if="sessions.length > 0"
            class="sessions-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px] w-full"
        >
            <li
                v-for="session in sessions"
                :key="session.id || session.unique_id"
                :data-page="session._page || 1"
            >
                <AgendaDetailCard
                    :session="session"
                    :year="year"
                    :is-full="!!session.is_full"
                    :action-required="!!session.user_added && session.session_reservations === true && !session.user_has_seat && !session.user_waitlisted"
                    :has-saved-seat="!!session.user_has_seat"
                    :recommendation="session.recommendation || ''"
                />
            </li>
        </ul>

        <!-- Empty state -->
        <div
            v-else
            class="flex flex-col items-center justify-center min-h-[20rem] text-(--magenta)"
        >
            <p class="font-hubspot-sans text-[16px]">No sessions found.</p>
            <p class="font-hubspot-sans text-[14px] text-(--dark-pink) mt-[8px]">Try adjusting your filters.</p>
        </div>

        <!-- Infinite scroll sentinel -->
        <div
            v-if="infinite && hasMore"
            ref="sentinel"
            class="relative flex items-center justify-center py-[2rem] min-h-[80px]"
            aria-hidden="true"
        >
            <FragmentsLoading :loading="loading" />
        </div>
    </div>
</template>

